import { ApiRequestOptions, ApiResponse } from '../types/api';
import axios from 'axios';
import { AUTH_REQUIRED_MESSAGE, dispatchAuthRequired } from '../utils/authPrompt';

const DEFAULT_API_BASE_URL = 'https://genai-wapp-beta-genai-pice-beta-sljsanbamz.cn-hangzhou.fcapp.run';

const resolveApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  return DEFAULT_API_BASE_URL;
};

class ApiClient {
  private baseURL = resolveApiBaseUrl();
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  private isAuthEndpoint(endpoint: string): boolean {
    return endpoint.startsWith('/api/v1/auth/');
  }

  private extractErrorMessage(error: any): string {
    return error.response?.data?.error
      || error.response?.data?.message
      || error.response?.data?.ErrorMessage
      || error.message;
  }

  private generateAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'x-acs-date': new Date().toISOString()
    };

    if (token) {
      headers['authorization'] = `Bearer ${token}`;
      headers['Authentication'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Subscribe to token refresh completion
   */
  private subscribeTokenRefresh(cb: (token: string) => void): void {
    this.refreshSubscribers.push(cb);
  }

  /**
   * Notify all subscribers that token has been refreshed
   */
  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach(cb => cb(token));
    this.refreshSubscribers = [];
  }

  /**
   * Check if the error is a JWT invalid/expired error
   */
  private isJWTError(error: any): boolean {
    const errorCode = error.response?.data?.ErrorCode;
    const errorMessage = error.response?.data?.ErrorMessage;

    return (
      errorCode === 'JWTTokenIsInvalid' ||
      errorCode === 'JWTTokenIsMissing' ||
      (errorMessage && errorMessage.includes('invalid or expired jwt')) ||
      (error.response?.status === 401 && localStorage.getItem('token'))
    );
  }

  private isAuthRequiredError(error: any): boolean {
    const status = error.response?.status;
    const errorMessage = String(this.extractErrorMessage(error) || '').toLowerCase();

    return (
      status === 401 ||
      errorMessage.includes('access token required') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('login required') ||
      errorMessage.includes('请先登录') ||
      errorMessage.includes('登录已过期')
    );
  }

  private buildFailureResponse<T>(error: any, endpoint: string, options?: ApiRequestOptions): ApiResponse<T> {
    if (!options?.suppressAuthPrompt && !this.isAuthEndpoint(endpoint) && this.isAuthRequiredError(error)) {
      dispatchAuthRequired({ reason: 'api-unauthorized' });
      return {
        success: false,
        error: AUTH_REQUIRED_MESSAGE,
        statusCode: 401
      };
    }

    return {
      success: false,
      error: this.extractErrorMessage(error),
      statusCode: error.response?.status
    };
  }

  /**
   * Attempt to refresh the access token using refresh token
   */
  private async refreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      console.log('No refresh token available');
      this.clearTokens();
      return null;
    }

    try {
      console.log('Attempting to refresh token...');
      const response = await axios.post(
        `${this.baseURL}/api/v1/auth/refresh`,
        { refreshToken },
        {
          headers: {
            'x-acs-date': new Date().toISOString(),
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.token) {
        const newToken = response.data.token;
        localStorage.setItem('token', newToken);
        console.log('Token refreshed successfully');
        return newToken;
      }

      return null;
    } catch (error: any) {
      console.error('Token refresh failed:', error.response?.data || error.message);
      // Clear tokens if refresh fails - user needs to re-login
      this.clearTokens();
      return null;
    }
  }

  /**
   * Clear all stored tokens
   */
  private clearTokens(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    // Dispatch auth change event so UI can update
    window.dispatchEvent(new Event('auth-change'));
  }

  /**
   * Handle JWT error by refreshing token and retrying the request
   */
  private async handleJWTError<T>(
    requestFn: () => Promise<ApiResponse<T>>,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    if (this.isRefreshing) {
      // Wait for token refresh to complete
      return new Promise((resolve) => {
        this.subscribeTokenRefresh(async () => {
          // Retry request with new token
          const result = await requestFn();
          resolve(result);
        });
      });
    }

    this.isRefreshing = true;

    try {
      const newToken = await this.refreshToken();

      if (newToken) {
        this.onTokenRefreshed(newToken);
        // Retry the original request
        return await requestFn();
      } else {
        this.clearTokens();
        if (!options?.suppressAuthPrompt) {
          dispatchAuthRequired({ reason: 'api-unauthorized' });
        }
        return {
          success: false,
          error: AUTH_REQUIRED_MESSAGE,
          statusCode: 401
        };
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  async get<T>(endpoint: string, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const separator = url.includes('?') ? '&' : '?';
        const cacheBustedUrl = `${url}${separator}_t=${Date.now()}`;
        const response = await axios.get(cacheBustedUrl, { headers: { ...authHeaders, ...headers } });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error; // Re-throw to trigger refresh
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }

  async post<T>(endpoint: string, body?: any, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const response = await axios.post(url, body, { headers: { ...authHeaders, ...headers } });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error; // Re-throw to trigger refresh
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }

  async postBlob(endpoint: string, body?: any, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<Blob>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<Blob>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const response = await axios.post(url, body, {
          headers: { ...authHeaders, ...headers },
          responseType: 'blob'
        });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error;
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }

  async postFormData<T>(endpoint: string, formData: FormData, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const response = await axios.post(url, formData, {
          headers: {
            ...authHeaders,
            ...headers
          }
        });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error;
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }

  async put<T>(endpoint: string, body?: any, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const response = await axios.put(url, body, { headers: { ...authHeaders, ...headers } });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error;
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const authHeaders = this.generateAuthHeaders();
        const response = await axios.delete(url, { headers: { ...authHeaders, ...headers } });
        return { data: response.data, success: true };
      } catch (error: any) {
        if (this.isJWTError(error)) {
          throw error;
        }
        return this.buildFailureResponse(error, endpoint, options);
      }
    };

    try {
      return await makeRequest();
    } catch (error: any) {
      if (this.isJWTError(error)) {
        return this.handleJWTError(makeRequest, options);
      }
      return this.buildFailureResponse(error, endpoint, options);
    }
  }
}

export const apiClient = new ApiClient();
